import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";
import Backdrop from "@mui/material/Backdrop";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

import { showToast } from "../../../store/actions";
import AddSAML from "./AddSAML";
import Mapping from "./Mapping";
import DownloadMetadata from "./DownloadMetadata";
import AuthProviderService from "../../../service/authProvider";
import { ButtonUI, KMKDialog } from "../../Common";

const useStyles = makeStyles()((theme) => ({
  Dialog: {
    margin: 5,
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
  iconContainer: {
    "& .MuiStepConnector-root": {
      width: "83.73px",
      maxWidth: "83.73px",
    },
  },
  container: {
    padding: 24,
    backgroundColor: "#FFFFFF",
  },
  [theme.breakpoints.down("510")]: {
    iconContainer: {
      display: "flex",
      flexWrap: "wrap",
      marginLeft: "10px",
    },
  },
}));

export default function AddForm({ setOpen, IDP_Id, loadIDPData }) {
  const [idpId, setIdpId] = useState(0);
  const [idpName, setIdpName] = useState("");
  const [MetaDataURL, setMetaDataURL] = useState("");
  const [email, setEmail] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [claimEmail, setClaimEmail] = useState("");
  const [claimFirstname, setClaimFirstname] = useState("");
  const [claimLastname, setClaimLastname] = useState("");
  const [claimValues, setClaimValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enable, setEnable] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  const { classes } = useStyles();
  const dispatch = useDispatch();

  const fieldObject1 = {
    idpName,
    setIdpName,
    MetaDataURL,
    setMetaDataURL,
  };

  const fieldObject2 = {
    email,
    setEmail,
    firstname,
    setFirstname,
    lastname,
    setLastname,
    claimEmail,
    setClaimEmail,
    claimFirstname,
    setClaimFirstname,
    claimLastname,
    setClaimLastname,
  };

  const fieldObject3 = {
    enable,
    setEnable,
  };

  const MainTitle = IDP_Id > 0 ? "Edit SAML Identity Provider" : "Add SAML Identity Provider";
  const succMsg = IDP_Id > 0 ? "Identity Provider Updated Successfully!" : "Identity Provider Added Successfully!";
  const Title1 = idpId > 0 ? "Edit Name and Metadata" : "Add Name and Metadata";
  const steps = [Title1, "Configure Attribute Mapping", "Download Metadata Document"];
  const action = IDP_Id === 0 ? "add" : "edit";

  const handleNext = async () => {
    setLoading(true);

    const authProviderService = new AuthProviderService();
    if (activeStep === 0) {
      const IDPData = {
        Name: idpName,
        MetaDataURL,
      };
      let response = null;
      if (idpId > 0) response = await authProviderService.updateSAMLIDPMetadata(idpId, IDPData);
      else response = await authProviderService.createIDP(IDPData);

      if (response.success) {
        if (idpId === 0) setIdpId(response.data.IDP_id);
        loadIDPData(action);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else if (activeStep === 1) {
      const data = {
        Email: {
          value: email,
          format: claimEmail,
        },
        FirstName: {
          value: firstname,
          format: claimFirstname,
        },
        LastName: {
          value: lastname,
          format: claimLastname,
        },
      };

      const response = await authProviderService.updateIDP(idpId, data);

      if (response.success) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else {
      const response = await authProviderService.updateIDP(idpId, {
        Status: enable ? 1 : 0,
      });

      if (response.success) {
        clearFormData();
        loadIDPData(action);
        dispatch(
          showToast({
            message: succMsg,
            type: "success",
          })
        );

        setOpen(false);
        setActiveStep(0);
      }
    }

    setLoading(false);
  };

  const downloadXML = async () => {
    setLoading(true);
    const authProviderService = new AuthProviderService();
    await authProviderService.downloadXML(idpId);
    setLoading(false);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getSAMLInfo = async () => {
    setLoading(true);

    const authProviderService = new AuthProviderService();
    const response = await authProviderService.init();

    if (response.success) setClaimValues(response.data);
    else setClaimValues([]);

    setLoading(false);
  };

  useEffect(() => {
    if (IDP_Id > 0) {
      setIdpId(IDP_Id);

      const getIDP = async () => {
        setLoading(true);

        const authProviderService = new AuthProviderService();
        const response = await authProviderService.getIDP(IDP_Id);

        if (response.success) {
          const IDPData = response.data;
          const mappingData = response.data.mappings;

          setIdpName(IDPData.Name);
          setMetaDataURL(IDPData.MetaDataURL);
          setEmail(mappingData.Email.value);
          setFirstname(mappingData.FirstName.value);
          setLastname(mappingData.LastName.value);
          setClaimEmail(mappingData.Email.format);
          setClaimFirstname(mappingData.FirstName.format);
          setClaimLastname(mappingData.LastName.format);
          setEnable(IDPData.Status);
        }

        setLoading(false);
      };
      getIDP();
    }
    getSAMLInfo();
  }, [IDP_Id]);

  const handleCancel = () => {
    setOpen(false);
  };

  const clearFormData = () => {
    setIdpName("");
    setMetaDataURL("");
    setEmail("");
    setFirstname("");
    setLastname("");
    setClaimEmail("");
    setClaimFirstname("");
    setClaimLastname("");
    setEnable(0);
  };

  return (
    <KMKDialog open={true} maxWidth="md" className={classes.Dialog}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>{MainTitle}</Toolbar>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box sx={{ width: "100%" }} className={classes.container}>
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>

        <Stepper activeStep={activeStep} className={classes.iconContainer}>
          {steps.map((label) => {
            const stepProps = {};
            const labelProps = {};

            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        <React.Fragment>
          {activeStep === 0 ? (
            <AddSAML {...fieldObject1} />
          ) : activeStep === 1 ? (
            <Mapping {...fieldObject2} claimValues={claimValues} />
          ) : (
            <DownloadMetadata {...fieldObject3} downloadXML={downloadXML} />
          )}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Box sx={{ flex: "1 1 auto" }} />
            <ButtonUI
              variant="contained"
              color="secondary"
              onClick={activeStep === 0 ? handleCancel : handleBack}
              sx={{ mr: 1 }}
            >
              {activeStep === 0 ? "Cancel" : "Back"}
            </ButtonUI>

            <ButtonUI
              variant="contained"
              color="success"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && !(idpName.trim() && MetaDataURL.trim())) ||
                (activeStep === 1 &&
                  !(
                    email.trim() &&
                    firstname.trim() &&
                    lastname.trim() &&
                    claimEmail &&
                    claimFirstname &&
                    claimLastname
                  )) ||
                loading
              }
            >
              {activeStep === steps.length - 1 ? "Finish" : "Save and Continue"}
            </ButtonUI>
          </Box>
        </React.Fragment>
      </Box>
    </KMKDialog>
  );
}
